import { plural } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { useFeatures } from '@jotta/grpc-connect-client/config'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jotta/ui/Dialog'

export interface DeletePhotoDialogProps {
  count?: number
  isLoading?: boolean
  open?: boolean
  onClose: (open: boolean) => void
  onDeletePhoto: () => void
}

export const DeletePhotoDialog: React.FC<DeletePhotoDialogProps> = ({
  open = false,
  count = 1,
  isLoading = false,
  onClose,
  onDeletePhoto,
}) => {
  const { photosSafeTrash } = useFeatures()
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="DeletePhotoDialog">
        <DialogHeader>
          <DialogTitle className="whitespace-normal">
            {plural(count, {
              one: 'Are you sure you want to delete the selected photo?',
              other: 'Are you sure you want to delete the # selected photos?',
            })}
          </DialogTitle>
        </DialogHeader>
        {photosSafeTrash ? (
          <div className="flex flex-col gap-3">
            <p className="body-sm-ln text-on-surface-variant">
              <Trans>
                Deleted photos are removed from all your synced devices, shares,
                and albums. You can restore them from trash for up to 30 days.
              </Trans>
            </p>
            <p className="body-sm-ln text-on-surface-variant">
              <Trans>
                Photos automatically backed up from your PC will be removed from
                this view, but they will not be deleted.
              </Trans>
            </p>
          </div>
        ) : (
          <p className="body-sm-ln text-on-surface-variant">
            <Trans>
              Deleted files are removed from all of your synced devices, and all
              your shares and albums. For a period of 30 days you can restore
              the deleted files by selecting "Trash" in the user menu.
            </Trans>
          </p>
        )}
        <DialogFooter>
          <button
            type="button"
            className="btn btn-destructive"
            data-testid="confirm"
            disabled={isLoading}
            aria-busy={isLoading}
            onClick={onDeletePhoto}
          >
            {photosSafeTrash
              ? plural(count, {
                  one: 'Delete photo',
                  other: 'Delete photos',
                })
              : plural(count, {
                  one: 'Yes, delete photo',
                  other: 'Yes, delete photos',
                })}
          </button>
          <DialogClose asChild>
            <button
              type="button"
              className="btn btn-elevated"
              data-testid="cancel"
              disabled={isLoading}
            >
              <Trans>Cancel</Trans>
            </button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
